/**
 * The dashboard's implementation of the shared {@link ScopePrefsStore}.
 *
 * Both tenancy axes have a user-editable default per resource, and this app keeps
 * them in the server-backed {@link useDashboardPrefs} blob (a `saved-views-api`
 * `user` row, cached locally in `ScopedStorage`). What a stored value MEANS —
 * whether it still names a tier this resource offers, and what to fall back to
 * when it does not — is `@authe/fe-commons/attribution`, so a rollout that
 * narrows the offered rungs cannot leave a stale preference being written at.
 *
 * That is the whole reason this is an adapter rather than two hooks with their own
 * resolution: the admin portal has different storage and must not have different
 * rules. `ui-core`'s scope hints and preferences panel are written against the
 * interface below, not against this file.
 *
 * The Transactions feed default lives beside it rather than in it: it is a
 * tenancy default the user sets in the same breath, but it is not a resolve tier
 * on this ladder — the explorer owns its own scope vocabulary. It gets the same
 * LAYERING all the same (workspace default under the user's own), because a
 * setting that is centrally managed on two axes out of three is one a workspace
 * admin cannot actually reason about.
 */
import * as React from "react";
import {
  effectiveScopes,
  type AttributionReadScope,
  type AttributionResource,
  type AttributionScope,
  type ResolvedScopeDefault,
  type ScopePrefsStore,
} from "@authe/fe-commons/attribution";
import { OVERLAY_CONFIG } from "@/config";
import { DEFAULT_TX_SCOPE_ID, type TxTenancyScopeId } from "@/features/transactions/scopes";
import { useDashboardPrefs } from "./dashboardPrefs";

export interface ScopePrefs extends ScopePrefsStore {
  /** Effective write tier for `resource`: a valid override, else this app's default. */
  resolveDefaultWriteScope: (resource: AttributionResource) => AttributionScope;
  /** Effective resolve tier for `resource`. */
  resolveDefaultResolveScope: (resource: AttributionResource) => AttributionReadScope;
  /** True when the CALLER's own choice — not an inherited layer — is in force. */
  isWriteOverridden: (resource: AttributionResource) => boolean;
  isResolveOverridden: (resource: AttributionResource) => boolean;
  /** Either axis with its provenance, for a surface that explains its defaults. */
  resolveWrite: (resource: AttributionResource) => ResolvedScopeDefault<AttributionScope>;
  resolveRead: (resource: AttributionResource) => ResolvedScopeDefault<AttributionReadScope>;

  /** The caller's OWN Transactions feed default, or `null` when they have none. */
  txFeedScope: TxTenancyScopeId | null;
  /** The feed default the layers beneath the caller imply. */
  inheritedTxFeedScope: TxTenancyScopeId;
  /** Effective Transactions feed default: own override, else inherited. */
  resolveDefaultTxFeedScope: () => TxTenancyScopeId;
  /** Persist the Transactions feed default; pass `null` to clear it. */
  setDefaultTxFeedScope: (scope: TxTenancyScopeId | null) => void;
}

/** The reactive per-resource tenancy defaults, resolved against this app's policy. */
export function useScopePrefs(): ScopePrefs {
  const { blob, managed, managedTxFeed, setWriteScope, setResolveScope, setTxFeed } =
    useDashboardPrefs();
  const { writeScope, resolveScope, txFeed } = blob;

  return React.useMemo<ScopePrefs>(() => {
    const store: ScopePrefsStore = {
      writeScope,
      resolveScope,
      managed,
      setWriteScope,
      setResolveScope,
    };
    const effective = effectiveScopes(store, OVERLAY_CONFIG);
    const inheritedTxFeedScope = managedTxFeed ?? DEFAULT_TX_SCOPE_ID;
    return {
      ...store,
      resolveDefaultWriteScope: effective.writeScope,
      resolveDefaultResolveScope: effective.readScope,
      isWriteOverridden: effective.isWriteOverridden,
      isResolveOverridden: effective.isReadOverridden,
      resolveWrite: effective.resolveWrite,
      resolveRead: effective.resolveRead,
      txFeedScope: txFeed,
      inheritedTxFeedScope,
      resolveDefaultTxFeedScope: () => txFeed ?? inheritedTxFeedScope,
      setDefaultTxFeedScope: setTxFeed,
    };
  }, [
    managed,
    managedTxFeed,
    resolveScope,
    setResolveScope,
    setTxFeed,
    setWriteScope,
    txFeed,
    writeScope,
  ]);
}
